import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Wallet } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const METHODS = [
  { id: 'check', label: 'Check' },
  { id: 'cash', label: 'Cash' },
  { id: 'card', label: 'Card' },
  { id: 'ach', label: 'ACH / Bank transfer' },
  { id: 'zelle', label: 'Zelle' },
  { id: 'other', label: 'Other' },
];
const money = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

// Staff record a check/cash/ACH payment against an invoice without leaving the CRM.
export const RecordPaymentDialog = ({ invoice, open, onOpenChange, token, onRecorded }) => {
  const balance = Math.max(0, Number(invoice?.balance_due ?? (invoice?.total || 0) - (invoice?.amount_paid || 0)));
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('check');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [sendReceipt, setSendReceipt] = useState(true);

  const reset = () => { setAmount(''); setMethod('check'); setReference(''); setNotes(''); setSendReceipt(true); };
  const close = (v) => { onOpenChange(v); if (!v) reset(); };

  const submit = async () => {
    const value = parseFloat(amount);
    if (!value || value <= 0) return toast.error('Enter a payment amount greater than 0');
    if (value > balance + 0.005) return toast.error(`Amount exceeds the outstanding balance of ${money(balance)}`);
    setSaving(true);
    try {
      const { data } = await axios.post(`${API_URL}/admin/invoices/${invoice.id}/payments`, { amount: value, method, reference, notes, send_receipt: sendReceipt }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${money(value)} recorded on ${invoice.invoice_number}${data?.receipt_sent ? ' · receipt emailed' : ''}`);
      onRecorded?.();
      close(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md" data-testid="record-payment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wallet className="w-5 h-5 text-emerald-600" /> Record payment</DialogTitle>
          <DialogDescription>
            {invoice?.invoice_number} · Outstanding <span className="font-mono font-medium text-neutral-900" data-testid="record-payment-balance">{money(balance)}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-500">Amount</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input type="number" min="0.01" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={balance.toFixed(2)} data-testid="record-payment-amount" />
                <Button type="button" variant="outline" size="sm" onClick={() => setAmount(balance.toFixed(2))} data-testid="record-payment-full">Full</Button>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-500">Method</Label>
              <select value={method} onChange={(e) => setMethod(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-neutral-200 px-3 text-sm bg-white" data-testid="record-payment-method">
                {METHODS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-neutral-500">Reference (check # / transaction id)</Label>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. Check #1042" className="mt-1" data-testid="record-payment-reference" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-neutral-500">Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1" data-testid="record-payment-notes" />
          </div>
          <label className="flex items-center gap-2 text-sm text-neutral-700 cursor-pointer">
            <input type="checkbox" checked={sendReceipt} onChange={(e) => setSendReceipt(e.target.checked)} className="h-4 w-4 accent-emerald-600" data-testid="record-payment-send-receipt" />
            Email a receipt to {invoice?.customer_email || 'the customer'}
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="record-payment-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Record payment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RecordPaymentDialog;
